import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Brain, ShieldCheck, Sparkles, CheckCircle2, PlayCircle, ArrowRight, Quote, Activity, Zap } from 'lucide-react';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
export function ValueProposition() {
  return <section className="py-24 lg:py-32 bg-white relative overflow-hidden" data-id="element-3697">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-3698">
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-primary-50/50 rounded-full blur-[120px] -translate-y-1/2" data-id="element-3699" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-purple-50/50 rounded-full blur-[100px] translate-y-1/2" data-id="element-3700" />
      </div>

      <div className="container-custom max-w-7xl mx-auto relative z-10" data-id="element-3701">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-3702">
          <EnhancedScrollReveal data-id="element-3703">
            <Badge variant="secondary" className="mb-6" data-id="element-3704">
              Filosofía Holística
            </Badge>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-charcoal-900 mb-6 leading-tight" data-id="element-3705">
              Nutrición para el cuerpo, <br data-id="element-3706" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-purple-600" data-id="element-3707">
                estímulo para la mente
              </span>
            </h2>
            <p className="text-lg md:text-xl text-charcoal-600 leading-relaxed font-light" data-id="element-3708">
              La salud integral no se logra solo con comida. Combinamos
              nutrición fresca de grado humano con enriquecimiento ambiental
              para un bienestar completo.
            </p>
          </EnhancedScrollReveal>
        </div>

        {/* Dual Pillars Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-24" data-id="element-3709">
          {/* Pillar 1: Nutrition (Body) */}
          <EnhancedScrollReveal direction="right" className="h-full" data-id="element-3710">
            <Card className="h-full p-8 md:p-10 bg-gradient-to-br from-[#FDFBF7] to-white border-primary-100 hover:border-primary-200 transition-all duration-500 group relative overflow-hidden" data-id="element-3711">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-primary-100/50 transition-colors" data-id="element-3712" />

              <div className="relative z-10" data-id="element-3713">
                <div className="w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center mb-8 shadow-sm group-hover:scale-110 transition-transform duration-500" data-id="element-3714">
                  <Heart className="w-8 h-8 text-primary-600" data-id="element-3715" />
                </div>

                <h3 className="text-3xl font-serif font-bold text-charcoal-900 mb-4" data-id="element-3716">
                  Salud Física
                </h3>
                <p className="text-charcoal-600 mb-8 leading-relaxed text-lg" data-id="element-3717">
                  Fórmulas frescas cocinadas al vacío que maximizan la absorción
                  de nutrientes y fortalecen el sistema inmune desde el
                  interior.
                </p>

                <ul className="space-y-4 mb-8" data-id="element-3718">
                  {[{
                  text: 'Ingredientes 100% Grado Humano',
                  icon: ShieldCheck
                }, {
                  text: 'Biodisponibilidad superior al 90%',
                  icon: Activity
                }, {
                  text: 'Sin procesados ni harinas',
                  icon: CheckCircle2
                }].map((item, i) => <li key={i} className="flex items-center gap-3 text-charcoal-700 font-medium" data-id="element-3719">
                      <item.icon className="w-5 h-5 text-primary-500" data-id="element-3720" />
                      {item.text}
                    </li>)}
                </ul>

                <div className="bg-white rounded-xl p-4 border border-primary-100 shadow-sm" data-id="element-3721">
                  <div className="flex items-start gap-3" data-id="element-3722">
                    <Quote className="w-5 h-5 text-primary-400 shrink-0" data-id="element-3723" />
                    <div data-id="element-3724">
                      <p className="text-sm text-charcoal-600 italic mb-2" data-id="element-3725">
                        "La dieta fresca reduce los marcadores inflamatorios en
                        sangre en solo 4 semanas."
                      </p>
                      <p className="text-xs font-bold text-primary-700" data-id="element-3726">
                        — Journal of Animal Physiology, 2022
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </EnhancedScrollReveal>

          {/* Pillar 2: Stimulation (Mind) */}
          <EnhancedScrollReveal direction="left" delay={0.2} className="h-full" data-id="element-3727">
            <Card className="h-full p-8 md:p-10 bg-gradient-to-br from-[#FDFBF7] to-white border-purple-100 hover:border-purple-200 transition-all duration-500 group relative overflow-hidden" data-id="element-3728">
              <div className="absolute top-0 right-0 w-64 h-64 bg-purple-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-purple-100/50 transition-colors" data-id="element-3729" />

              <div className="relative z-10" data-id="element-3730">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mb-8 shadow-sm group-hover:scale-110 transition-transform duration-500" data-id="element-3731">
                  <Brain className="w-8 h-8 text-purple-600" data-id="element-3732" />
                </div>

                <h3 className="text-3xl font-serif font-bold text-charcoal-900 mb-4" data-id="element-3733">
                  Salud Mental
                </h3>
                <p className="text-charcoal-600 mb-8 leading-relaxed text-lg" data-id="element-3734">
                  Desafíos cognitivos que reducen la ansiedad y el aburrimiento,
                  promoviendo un comportamiento equilibrado y feliz.
                </p>

                <ul className="space-y-4 mb-8" data-id="element-3735">
                  {[{
                  text: 'Reducción de cortisol (estrés)',
                  icon: Sparkles
                }, {
                  text: 'Prevención del deterioro cognitivo',
                  icon: Zap
                }, {
                  text: 'Resolución de problemas',
                  icon: CheckCircle2
                }].map((item, i) => <li key={i} className="flex items-center gap-3 text-charcoal-700 font-medium" data-id="element-3736">
                      <item.icon className="w-5 h-5 text-purple-500" data-id="element-3737" />
                      {item.text}
                    </li>)}
                </ul>

                <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm" data-id="element-3738">
                  <div className="flex items-start gap-3" data-id="element-3739">
                    <Quote className="w-5 h-5 text-purple-400 shrink-0" data-id="element-3740" />
                    <div data-id="element-3741">
                      <p className="text-sm text-charcoal-600 italic mb-2" data-id="element-3742">
                        "20 minutos de estimulación mental equivalen a 1 hora de
                        ejercicio físico intenso."
                      </p>
                      <p className="text-xs font-bold text-purple-700" data-id="element-3743">
                        — Applied Animal Behaviour Science
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </EnhancedScrollReveal>
        </div>

        {/* Comparison Section */}
        <EnhancedScrollReveal delay={0.3} data-id="element-3744">
          <div className="bg-charcoal-900 rounded-[2.5rem] p-8 md:p-12 lg:p-16 text-white relative overflow-hidden shadow-2xl" data-id="element-3745">
            {/* Abstract Background */}
            <div className="absolute inset-0 opacity-20" data-id="element-3746">
              <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-700 via-charcoal-900 to-charcoal-900" data-id="element-3747" />
            </div>

            <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center" data-id="element-3748">
              <div data-id="element-3749">
                <h3 className="text-3xl md:text-4xl font-serif font-bold mb-6" data-id="element-3750">
                  El Efecto Sinergia
                </h3>
                <p className="text-gray-300 text-lg mb-8 leading-relaxed" data-id="element-3751">
                  Cuando combinas una dieta antiinflamatoria con una mente
                  activa, los resultados se multiplican. No es solo sumar
                  beneficios, es transformar la vida de tu mascota.
                </p>

                <div className="grid grid-cols-2 gap-6 mb-8" data-id="element-3752">
                  <div data-id="element-3753">
                    <div className="text-3xl font-bold text-primary-400 mb-1" data-id="element-3754">
                      +40%
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-3755">
                      Esperanza de vida saludable
                    </div>
                  </div>
                  <div data-id="element-3756">
                    <div className="text-3xl font-bold text-purple-400 mb-1" data-id="element-3757">
                      -60%
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-3758">
                      Visitas al veterinario
                    </div>
                  </div>
                </div>

                <Button variant="gradient" size="lg" className="rounded-full px-8" data-id="element-3759">
                  Ver Planes Personalizados{' '}
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-3760" />
                </Button>
              </div>

              {/* Visual Comparison Graph Representation */}
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10" data-id="element-3761">
                <div className="space-y-6" data-id="element-3762">
                  <div data-id="element-3763">
                    <div className="flex justify-between text-sm font-bold mb-2" data-id="element-3764">
                      <span className="text-white" data-id="element-3765">Enfoque Petiora</span>
                      <span className="text-primary-400" data-id="element-3766">Óptimo</span>
                    </div>
                    <div className="h-3 bg-gray-700 rounded-full overflow-hidden" data-id="element-3767">
                      <motion.div initial={{
                      width: 0
                    }} whileInView={{
                      width: '95%'
                    }} transition={{
                      duration: 1.5,
                      ease: 'easeOut'
                    }} className="h-full bg-gradient-to-r from-primary-500 to-purple-500" data-id="element-3768" />
                    </div>
                  </div>

                  <div data-id="element-3769">
                    <div className="flex justify-between text-sm font-medium mb-2" data-id="element-3770">
                      <span className="text-gray-400" data-id="element-3771">Solo Comida Fresca</span>
                      <span className="text-gray-400" data-id="element-3772">Bueno</span>
                    </div>
                    <div className="h-3 bg-gray-700 rounded-full overflow-hidden" data-id="element-3773">
                      <motion.div initial={{
                      width: 0
                    }} whileInView={{
                      width: '70%'
                    }} transition={{
                      duration: 1.5,
                      delay: 0.2,
                      ease: 'easeOut'
                    }} className="h-full bg-primary-500/60" data-id="element-3774" />
                    </div>
                  </div>

                  <div data-id="element-3775">
                    <div className="flex justify-between text-sm font-medium mb-2" data-id="element-3776">
                      <span className="text-gray-400" data-id="element-3777">Pienso Tradicional</span>
                      <span className="text-gray-400" data-id="element-3778">Básico</span>
                    </div>
                    <div className="h-3 bg-gray-700 rounded-full overflow-hidden" data-id="element-3779">
                      <motion.div initial={{
                      width: 0
                    }} whileInView={{
                      width: '30%'
                    }} transition={{
                      duration: 1.5,
                      delay: 0.4,
                      ease: 'easeOut'
                    }} className="h-full bg-gray-500" data-id="element-3780" />
                    </div>
                  </div>
                </div>

                <p className="mt-6 text-xs text-center text-gray-500" data-id="element-3781">
                  *Basado en estudio interno de calidad de vida (2023)
                </p>
              </div>
            </div>
          </div>
        </EnhancedScrollReveal>
      </div>
    </section>;
}